import { SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import FormField from '@components/Common/FormField';
import { PageHero } from '@components/Common/Panel';
import { useToast } from '@components/Common/Toast/ToastProvider';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import LockIcon from '@mui/icons-material/Lock';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import type { RootState } from '@store/rootReducer';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeMode, type ThemePreference } from '../../theme/ThemeModeProvider';
import './index.scss';

/*
 * Three choices, and `system` is one of them rather than the absence of one. A two-way toggle
 * makes someone whose machine is light sit in a dark console until they find this page, which is
 * the state most of them would be in.
 */
const THEME_CHOICES: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: 'Light', icon: <LightModeOutlinedIcon fontSize="small" /> },
    { value: 'dark', label: 'Dark', icon: <DarkModeOutlinedIcon fontSize="small" /> },
    { value: 'system', label: 'System', icon: <SettingsBrightnessOutlinedIcon fontSize="small" /> },
];

export interface UserFormData {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    address?: string;
    city?: string;
    country?: string;
    state?: string;
    phone?: string;
    zipcode?: string;
    fullName?: string;
    password?: string;
}

const UserDetails: React.FC = () => {
    const dispatch = useDispatch();
    const profile = useSelector((state: RootState) => state.loginUserProfile.profile);
    
    // Form state
    const { showToast } = useToast();
    const { preference, mode, setPreference } = useThemeMode();
    const [firstName, setFirstName] = useState(profile?.personalDetails?.firstName || '');
    const [lastName, setLastName] = useState(profile?.personalDetails?.lastName || '');
    const [email, setEmail] = useState(profile?.personalDetails?.email || '');
    const [isEditing, setIsEditing] = useState(false);

    // Password popup state
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Feedback state
    const [isLoading, setIsLoading] = useState(false);

    // Sync local state with Redux profile changes
    React.useEffect(() => {
        setFirstName(profile?.personalDetails?.firstName || '');
        setLastName(profile?.personalDetails?.lastName || '');
        setEmail(profile?.personalDetails?.email || '');
    }, [profile]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        // Reset to original values
        setFirstName(profile?.personalDetails?.firstName || '');
        setLastName(profile?.personalDetails?.lastName || '');
        setEmail(profile?.personalDetails?.email || '');
    };
    const handleSave = async () => {
        setIsLoading(true);
        
        try {
            // Our own route, not updateUserById through the proxy: that mutation is Super Admin
            // only, because it can also change a role. This one changes your name and nothing else.
            const res = await fetch('/api/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ firstName, lastName }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                showToast('Profile updated', 'success');
                setIsEditing(false);
                // Re-read the profile so the sidebar and this form show the new name.
                dispatch({ type: 'loginUserProfile/fetchUserProfileFromSessionRequest', payload: { id: profile._id } });
            } else {
                showToast(data.error || 'Your name could not be saved. Try again.', 'error');
            }
        } catch {
            showToast('Your name could not be saved. Try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenPasswordDialog = () => {
        setOpenPasswordDialog(true);
        setCurrentPassword('');
        setNewPassword('');
        setPasswordError('');
    };

    const handleClosePasswordDialog = () => {
        setOpenPasswordDialog(false);
        setCurrentPassword('');
        setNewPassword('');
        setPasswordError('');
    };

    const handleUpdatePassword = () => {
        // Validation
        if (!currentPassword || !newPassword) {
            setPasswordError('Please enter both current and new password.');
            return;
        }
        if (newPassword.length < 12) {
            setPasswordError('New password must be at least 12 characters long.');
            return;
        }
        if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
            setPasswordError('New password must contain uppercase, lowercase, and a number.');
            return;
        }
        
        // Call backend API to update password
        fetch('/api/me/update-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                currentPassword,
                newPassword,
            }),
        })
            .then(async res => {
                const data = await res.json();
                if (res.ok && data.success) {
                    setPasswordError('');
                    setOpenPasswordDialog(false);
                    showToast('Profile updated', 'success');
                } else {
                    setPasswordError(data.error || 'Failed to update password.');
                }
            })
            .catch(() => {
                setPasswordError('Failed to update password.');
            });
    };

    return (
        <Box className="cc-account">
            <PageHero
                kicker="Your account"
                title={`${firstName} ${lastName}`.trim() || 'Your profile'}
                text="Your name is what colleagues see on notes, assignments and audit trails."
            />

            <SectionCard
                title="Personal details"
                icon={<PersonOutlineIcon />}
                meta="3 fields"
                editing={isEditing}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSave}
            >
                <WorkspaceField
                    label="First name"
                    value={firstName}
                    editing={isEditing}
                    edit={(
                        <WorkspaceInput value={firstName} onChange={setFirstName} />
                    )}
                />
                <WorkspaceField
                    label="Last name"
                    value={lastName}
                    editing={isEditing}
                    edit={(
                        <WorkspaceInput value={lastName} onChange={setLastName} />
                    )}
                />
                {/* Read-only in both states: the email is the login identity and
                    the mutation does not accept a change to it. */}
                <WorkspaceField
                    label="Email"
                    value={email}
                    mono
                    full
                />
                <WorkspaceField
                    label=""
                    full
                    value={<span className="cc-account__note">Your email is your sign-in and cannot be changed here.</span>}
                />
            </SectionCard>

            <SectionCard title="Account security" icon={<LockIcon />}>
                <Box className="cc-account__security">
                    <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        onClick={handleOpenPasswordDialog}
                        disabled={isLoading}
                    >
                        Update password
                    </Button>
                    <span className="cc-account__note">
                        At least 12 characters, with an uppercase letter, a lowercase
                        letter and a number.
                    </span>
                </Box>
            </SectionCard>

            <SectionCard title="Appearance" icon={<SettingsBrightnessOutlinedIcon />}>
                <Box className="cc-account__appearance">
                    <ToggleButtonGroup
                        exclusive
                        value={preference}
                        aria-label="Theme"
                        className="cc-account__theme-group"
                        /* Saved on change, like the notification switches. A Save button for a
                           control whose effect is instantly visible asks the user to confirm
                           something they can already see. */
                        onChange={(_e, next: ThemePreference | null) => {
                            if (!next) return;
                            setPreference(next);
                        }}
                    >
                        {THEME_CHOICES.map((choice) => (
                            <ToggleButton key={choice.value} value={choice.value} aria-label={choice.label}>
                                {choice.icon}
                                {choice.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <span className="cc-account__note">
                        {preference === 'system'
                            ? `Following your device, which is currently ${mode}.`
                            : 'Applies to this account on every device you sign in from.'}
                    </span>
                </Box>
            </SectionCard>

            {/*
                Toasts come from `useToast`, not from a Snackbar mounted here. This file had two
                of its own, which made three toast mechanisms in the console: these, the shared
                provider, and the redux `loaderStatus` bar the GraphQL helper drives. Two of the
                three could fire at once, from different corners of the screen.
            */}
            {/* Password Update Dialog */}
            <Dialog
                open={openPasswordDialog}
                onClose={handleClosePasswordDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Password</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={12}>
                            <FormField
                              required
                                label="Current Password"
                                name="currentPassword"
                                type="password"
                                value={currentPassword}
                                onChange={(_, value) => setCurrentPassword(value)}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FormField
                              required
                                label="New Password"
                                name="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(_, value) => setNewPassword(value)}
                                helperText="Min 8 chars with uppercase, lowercase, and a number"
                            />
                        </Grid>
                        {passwordError && (
                            <Grid size={12}>
                                <Alert severity="error">{passwordError}</Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleClosePasswordDialog} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdatePassword}
                        variant="contained"
                        disabled={!currentPassword || !newPassword}
                    >
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserDetails;
